<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../Button.svelte';
  import Textarea from '../../Textarea.svelte';
  import Notification from '../../Notification.svelte';
  import StatusBadge from '../../StatusBadge.svelte';
  import { DonationRequestStatusEnum } from '../../../lib/api/admin/donation.service';
  import {
    CharityDonationRequestService,
    type CharityDonationRequest,
    DonationRequestStatusEnum as CharityDonationRequestStatusEnum
  } from '../../../lib/api/charityServices/charity-donation-request.service';
  import { DonorService } from '../../../lib/api/charityServices/donor';
  import { currentDonationRequest, clearCurrentDonation } from '../../../lib/stores/donation-request.store';
  import { push } from 'svelte-spa-router';
  import BackButton from '../../BackButton.svelte';
  import Stepper from '../../Stepper.svelte';
  import StageBadge from '../../StageBadge.svelte';
  import { type LifecycleStage } from '../../../lib/donationLifecycle';
  import {
    getCharityDonationDetailStatus,
    isPaidOutToCharity,
    type CharityDonationDetailStatus,
  } from '../../../lib/utils/charity-donation-status';

  export let params: { id: string };

  let internalNote = '';
  let donation: CharityDonationRequest | null = null;
  let loading = false;
  let downloadingPdf = false;
  let markingReceived = false;
  let resendingReceipt = false;
  let showMarkReceivedConfirm = false;
  let error: string | null = null;

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Charity-specific status helpers
  type CharityStatus = CharityDonationDetailStatus;

  function getCharityStatus(d: CharityDonationRequest): CharityStatus {
    return getCharityDonationDetailStatus(d);
  }

  $: charityStatus = donation ? getCharityStatus(donation) : 'Pending';

  // Canonical lifecycle stage for the progress stepper (prefer backend value).
  function deriveStage(d: CharityDonationRequest | null): LifecycleStage {
    if (!d) return 'submitted';
    if (d.lifecycle_stage) return d.lifecycle_stage as LifecycleStage;
    const name = (d.donation_request_status?.name || d.status || '').toLowerCase();
    if (name === 'rejected' || name === 'declined') return 'rejected';
    if (name === 'pending') return 'submitted';
    if (d.charity_received_at) return 'received_by_charity';
    if (isPaidOutToCharity(d)) return 'paid_to_charity';
    if (d.payment_status === 'paid') return 'employer_paid';
    return 'approved';
  }

  $: stage = deriveStage(donation);

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  // Subscribe to the store
  const unsubscribe = currentDonationRequest.subscribe(value => {
    donation = value;
  });

  onMount(() => {
    // If donation is not in store, try to load it from API
    if (!donation) {
      loadDonationRequest();
    }

    // Clean up subscription when component is destroyed
    return () => {
      unsubscribe();
    };
  });

  async function loadDonationRequest() {
    if (!params?.id) {
      error = 'No donation request ID provided';
      return;
    }

    loading = true;
    error = null;

    try {
      const result = await CharityDonationRequestService.getDonationRequest(params.id);
      
      if (result.success && result.donation) {
        donation = result.donation;
      } else {
        error = result.error || 'Failed to load donation request';
      }
    } catch (err) {
      error = 'An error occurred while loading the donation request';
    } finally {
      loading = false;
    }
  }

  function formatDate(dateString: string | undefined) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  }

  function formatCurrency(value: number | undefined) {
    if (value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  async function markAsVerified() {
    if (!donation) return;
    
    try {
      const result = await CharityDonationRequestService.updateDonationRequestStatus(
        donation.uuid, 
        CharityDonationRequestStatusEnum.APPROVED
      );
      
      if (result.success) {
        // Reload the donation request to get updated data
        await loadDonationRequest();
        showNotification('Donation request approved successfully', 'success');
      } else {
        showNotification('Failed to update status', 'error');
      }
    } catch (err) {
      showNotification('An error occurred while updating status', 'error');
    }
  }

  async function downloadReceipt() {
    if (!donation) {
      showNotification('No donation request available', 'error');
      return;
    }

    downloadingPdf = true;
    
    try {
      const result = await DonorService.downloadDonationReceipt(donation.uuid);
      
      if (result.success) {
        showNotification('Receipt downloaded successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to download receipt. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      showNotification('Failed to download receipt. Please try again.', 'error');
    } finally {
      downloadingPdf = false;
    }
  }

  async function resendReceipt() {
    if (!donation) return;

    resendingReceipt = true;
    try {
      const result = await CharityDonationRequestService.resendReceipt(donation.uuid);
      if (result.success) {
        showNotification(result.message || 'Receipt sent to employee.', 'success');
      } else {
        showNotification(result.error || 'Failed to resend receipt.', 'error');
      }
    } catch (err) {
      showNotification('An error occurred. Please try again.', 'error');
    } finally {
      resendingReceipt = false;
    }
  }

  async function markAsReceived() {
    if (!donation) return;

    markingReceived = true;
    try {
      const result = await CharityDonationRequestService.markAsReceived(donation.uuid);
      if (result.success) {
        if (result.donation) {
          donation = result.donation;
        } else {
          await loadDonationRequest();
        }
        showMarkReceivedConfirm = false;
        showNotification('Donation marked as received successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to mark as received', 'error');
      }
    } catch (err) {
      showNotification('An error occurred. Please try again.', 'error');
    } finally {
      markingReceived = false;
    }
  }

  function openMarkReceivedConfirm() {
    showMarkReceivedConfirm = true;
  }

  function closeMarkReceivedConfirm() {
    if (!markingReceived) {
      showMarkReceivedConfirm = false;
    }
  }

  function goBack() {
    // Clear the current donation when navigating back
    clearCurrentDonation();
    window.history.back();
  }

  // Helper functions to extract data from various locations in the response
  function getEmployerName(donation: CharityDonationRequest): string {
    return donation.employer?.name || donation.employer_name || 'N/A';
  }

  function getUserName(donation: CharityDonationRequest): string {
    return donation.user?.full_name || donation.user_name || 'Unknown User';
  }

  function getUserEmail(donation: CharityDonationRequest): string {
    return donation.user?.email || donation.email || 'N/A';
  }

  function getHours(donation: CharityDonationRequest): number {
    return donation.leave_hours || donation.hours || donation.employer?.leave_hours || 0;
  }

  function getValue(donation: CharityDonationRequest): number {
    return donation.leave_value || donation.value || donation.employer?.leave_value || 0;
  }

  function getDate(donation: CharityDonationRequest): string {
    return donation.created_at || donation.date || 'N/A';
  }

  function getStatusName(donation: CharityDonationRequest): DonationRequestStatusEnum {
    const statusName = donation.donation_request_status?.name || donation.status || 'Pending';
    // Map status name to enum value
    const statusMap: Record<string, DonationRequestStatusEnum> = {
      'Pending': DonationRequestStatusEnum.PENDING,
      'Approved': DonationRequestStatusEnum.APPROVED,
      'Rejected': DonationRequestStatusEnum.REJECTED,
      'Processed': DonationRequestStatusEnum.PROCESSED,
    };
    return statusMap[statusName] || DonationRequestStatusEnum.PENDING;
  }
</script>

<div class="donation-detail-page">
  <BackButton text="Back" onClick={() => window.history.back()} />
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donation request details...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h3 class="error-title">Failed to Load Donation Request</h3>
      <p class="error-message">{error}</p>
      <div class="error-actions">
        <Button variant="secondary" on:click={() => window.history.back()}>Back</Button>
        <Button variant="primary" on:click={loadDonationRequest}>Try Again</Button>
      </div>
    </div>
  {:else if donation}
    <div class="detail-container">
      <!-- Donation Request Information -->
      <div class="info-section">
        <h2>Donation Request Details</h2>
        <div class="stepper-wrapper">
          <Stepper {stage} />
        </div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Status</span>
            <div class="status-wrapper">
              <StageBadge {stage} />
            </div>
          </div>
          <div class="info-item">
            <span class="info-label">Hours Donated</span>
            <span class="value-large">{getHours(donation)} hours</span>
          </div>
          <div class="info-item">
            <span class="info-label">Monetary Value</span>
            <span class="value-large">{formatCurrency(getValue(donation))}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Request Date</span>
            <span>{formatDate(getDate(donation))}</span>
          </div>
          {#if donation.updated_at}
            <div class="info-item">
              <span class="info-label">Last Updated</span>
              <span>{formatDate(donation.updated_at)}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Donor Information Section -->
      <div class="info-section">
        <h2>Donor Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Donor Name</span>
            <span>{getUserName(donation)}</span>
          </div>
          {#if charityStatus === 'Paid - Received'}
            <div class="info-item">
              <span class="info-label">Employee Email</span>
              <span>
                {#if getUserEmail(donation) !== 'N/A'}
                  <a href="mailto:{getUserEmail(donation)}" class="email-link">
                    {getUserEmail(donation)}
                  </a>
                {:else}
                  N/A
                {/if}
              </span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Employer Information Section -->
      <div class="info-section">
        <h2>Employer Information</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Employer Name</span>
            {#if donation.employer?.uuid}
              {@const employerUuid = donation.employer.uuid}
              <span>
                <button 
                  class="clickable-name"
                  on:click={() => push(`/charity/view-employer/${employerUuid}`)}
                  on:keydown={(e) => e.key === 'Enter' && push(`/charity/view-employer/${employerUuid}`)}
                  aria-label="View all donations from this employer"
                  type="button"
                >
                  {getEmployerName(donation)}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                </button>
              </span>
            {:else}
              <span>{getEmployerName(donation)}</span>
            {/if}
          </div>
          {#if donation.employer?.abn}
            <div class="info-item">
              <span class="info-label">ABN</span>
              <span>{donation.employer.abn}</span>
            </div>
          {/if}
          {#if donation.employer?.contact_email}
            <div class="info-item">
              <span class="info-label">Contact Email</span>
              <span>
                <a href="mailto:{donation.employer.contact_email}" class="email-link">
                  {donation.employer.contact_email}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.employer?.contact_phone}
            <div class="info-item">
              <span class="info-label">Contact Phone</span>
              <span>
                <a href="tel:{donation.employer.contact_phone}" class="phone-link">
                  {donation.employer.contact_phone}
                </a>
              </span>
            </div>
          {/if}
          {#if donation.employer?.contact_name}
            <div class="info-item">
              <span class="info-label">Contact Name</span>
              <span>{donation.employer.contact_name}</span>
            </div>
          {/if}
          {#if donation.employer?.head_office_location}
            <div class="info-item">
              <span class="info-label">Head Office Location</span>
              <span>{donation.employer.head_office_location}</span>
            </div>
          {/if}
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        {#if charityStatus === 'Waiting for Payment'}
          <Button variant="primary" on:click={openMarkReceivedConfirm} disabled={markingReceived}>
            {markingReceived ? 'Saving...' : 'Mark as Received'}
          </Button>
          {#if donation.payout_uuid}
            <Button variant="secondary" on:click={() => push(`/payout-detail/${donation?.payout_uuid}`)}>
              View Payout
            </Button>
          {/if}
        {/if}
        {#if charityStatus === 'Paid - Received'}
          <Button variant="secondary" on:click={resendReceipt} disabled={resendingReceipt}>
            {resendingReceipt ? 'Sending...' : 'Resend Invoice Email to Employee'}
          </Button>
          <Button variant="secondary" on:click={downloadReceipt} disabled={downloadingPdf}>
            {downloadingPdf ? 'Downloading...' : 'Download Receipt (PDF)'}
          </Button>
        {/if}
      </div>
    </div>
  {:else}
    <div class="error-container">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h3 class="error-title">Donation Request Not Found</h3>
      <p class="error-message">The donation request you're looking for could not be found.</p>
      <div class="error-actions">
        <Button variant="primary" on:click={() => window.history.back()}>Back</Button>
      </div>
    </div>
  {/if}
</div>

{#if showMarkReceivedConfirm}
  <div
    class="confirm-modal-backdrop"
    role="presentation"
    on:click|self={closeMarkReceivedConfirm}
    on:keydown={(e) => e.key === 'Escape' && closeMarkReceivedConfirm()}
  >
    <div class="confirm-modal-card" role="dialog" aria-modal="true" aria-labelledby="mark-received-confirm-title">
      <h2 id="mark-received-confirm-title" class="confirm-modal-title">Confirm mark as received</h2>
      <p class="confirm-modal-desc">
        Have you received this donation in your bank account? Marking it as received will notify the employee and update the donation status.
      </p>
      <div class="confirm-modal-actions">
        <button type="button" class="confirm-modal-cancel" on:click={closeMarkReceivedConfirm} disabled={markingReceived}>
          Cancel
        </button>
        <button type="button" class="confirm-modal-confirm" on:click={markAsReceived} disabled={markingReceived}>
          {markingReceived ? 'Saving...' : 'Yes, mark as received'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Notification Component -->
<Notification 
  show={notificationShow} 
  type={notificationType} 
  message={notificationMessage} 
/>

<style>
  .donation-detail-page {
    padding: 2rem;
    max-width: 1000px;
    margin: 0 auto;
    background: #f7fafc;
    min-height: 100vh;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
  }

  .error-icon {
    color: var(--color-negative, #dc2626);
    margin-bottom: 1rem;
  }

  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .error-message {
    color: #6b7280;
    font-size: 1rem;
    margin-bottom: 1.5rem;
    max-width: 500px;
  }

  .error-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .info-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .info-section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-item .info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
  }

  .info-item span {
    font-size: 1rem;
    color: #111827;
    font-weight: 400;
  }

  .value-large {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-positive, #059669);
  }

  .status-wrapper {
    display: inline-flex;
  }

  .stepper-wrapper {
    margin: 0 0 1.75rem 0;
  }

  .clickable-name {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    padding: 0;
    color: var(--color-primary, #3b82f6);
    font-size: 1rem;
    font-weight: 400;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
  }

  .clickable-name:hover {
    color: var(--color-primary-hover, #2563eb);
    text-decoration: underline;
  }

  .clickable-name:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-radius: 4px;
  }

  .clickable-name svg {
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s, transform 0.2s;
  }

  .clickable-name:hover svg {
    opacity: 1;
    transform: translate(2px, -2px);
  }

  .action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding-top: 1rem;
  }

  .action-buttons :global(button) {
    min-width: 200px;
  }

  .confirm-modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .confirm-modal-card {
    background: white;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 90%;
    padding: 1.5rem;
  }

  .confirm-modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.75rem;
  }

  .confirm-modal-desc {
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0 0 1.25rem;
  }

  .confirm-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  .confirm-modal-cancel,
  .confirm-modal-confirm {
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius-button, 10px);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
  }

  .confirm-modal-cancel {
    background: #f3f4f6;
    color: #374151;
  }

  .confirm-modal-confirm {
    background: var(--color-primary, #ffb400);
    color: #2d3748;
  }

  .confirm-modal-cancel:disabled,
  .confirm-modal-confirm:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    .donation-detail-page {
      padding: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .action-buttons {
      flex-direction: column;
    }

    .action-buttons :global(button) {
      width: 100%;
    }
  }
</style>